import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Image, Plus, RefreshCw, Download, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to build full photo URL
function getPhotoUrl(url) {
  if (!url) return '';
  // If URL is already absolute, return as is
  if (url.startsWith('http')) return url;
  // If URL is relative /api/..., prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
}

// Compress image client-side before upload
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const maxSize = 2000; // max dimension
    const quality = 0.85;
    
    // Create image element
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      
      // Set canvas size and draw
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            reject(new Error('Compression failed'));
          }
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Handle EXIF orientation by using createImageBitmap if available
    if (typeof createImageBitmap !== 'undefined') {
      createImageBitmap(file, { imageOrientation: 'from-image' })
        .then((bitmap) => {
          let { width, height } = bitmap;
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(bitmap, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        })
        .catch(() => {
          // Fallback to img element
          img.src = URL.createObjectURL(file);
        });
    } else {
      img.src = URL.createObjectURL(file);
    }
  });
}

// Upload single photo
async function uploadPhoto(file) {
  const formData = new FormData();
  formData.append('foto', file);
  
  const response = await fetch(`${API}/photos/upload`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Upload fallito');
  }
  
  return response.json();
}

export default function PhotoWall() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [lastUploadedPhoto, setLastUploadedPhoto] = useState(null);
  
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  // Check for admin key in URL
  const urlParams = new URLSearchParams(window.location.search);
  const adminKey = urlParams.get('key');
  const isAdmin = !!adminKey;

  // Fetch photos
  const fetchPhotos = useCallback(async (incremental = false) => {
    try {
      const lastTimestamp = incremental && photos.length > 0 ? photos[0].created_at : null;
      const url = lastTimestamp ? `${API}/photos?after=${encodeURIComponent(lastTimestamp)}` : `${API}/photos`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      
      if (incremental && data.photos.length > 0) {
        setPhotos(prev => [...data.photos, ...prev]);
        toast.success(`${data.photos.length} nuove foto!`);
      } else if (!incremental) {
        setPhotos(data.photos);
      } else {
        toast.info('Nessuna nuova foto');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Errore nel caricamento');
    }
  }, [photos]);

  // Initial load
  useEffect(() => {
    fetchPhotos().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle file selection
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    setShowFabMenu(false);
    
    const fileArray = Array.from(files);
    
    for (let i = 0; i < fileArray.length; i++) {
      setUploadProgress({ current: i + 1, total: fileArray.length });
      
      try {
        // Compress image
        const compressed = await compressImage(fileArray[i]);
        
        // Upload
        const result = await uploadPhoto(compressed);
        
        // Add to gallery immediately
        setPhotos(prev => [result, ...prev]);
        
        // Show toast with preview for last uploaded photo
        setLastUploadedPhoto(result);
        toast.success(
          <div className="flex items-center gap-3">
            <img src={getPhotoUrl(result.url)} alt="" className="w-12 h-12 object-cover rounded" />
            <span>Foto caricata!</span>
          </div>,
          { duration: 3000 }
        );
        
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Errore foto ${i + 1}: ${error.message}`);
      }
    }
    
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPhotos(true);
    setRefreshing(false);
  };

  // Handle delete (admin only)
  const handleDelete = async (photoId) => {
    if (!isAdmin || !adminKey) return;
    
    if (!window.confirm('Eliminare questa foto?')) return;
    
    try {
      const response = await fetch(`${API}/photos/${photoId}?key=${encodeURIComponent(adminKey)}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Eliminazione fallita');
      }
      
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setLightboxPhoto(null);
      toast.success('Foto eliminata');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Hero Section */}
      <header className="pt-12 pb-8 px-6 text-center">
        <h1 className="font-heading text-4xl text-[#72260c] mb-2">
          Lelia & Stefano
        </h1>
        <p className="font-body text-sm text-[#6a7263] tracking-wide mb-4">
          18 settembre 2026
        </p>
        <p className="font-body text-[#4b453a] text-sm leading-relaxed max-w-sm mx-auto">
          Aiutaci a raccogliere momenti indimenticabili da un'altra prospettiva, la tua! 
          Aggiungi gli scatti che preferisci alla nostra parete condivisa
        </p>
      </header>

      {/* Action Buttons */}
      <div className="px-6 pb-6 space-y-3 max-w-md mx-auto">
        {/* Camera Button - Salvia color */}
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 bg-[#6a7263] text-white rounded-xl font-body font-medium text-base transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          <Camera className="w-5 h-5" />
          <span>Scatta una foto</span>
        </button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />

        {/* Gallery Button - Outline */}
        <button
          onClick={() => galleryInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-3 py-4 px-6 border-2 border-[#6a7263] text-[#6a7263] rounded-xl font-body font-medium text-base transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          <Image className="w-5 h-5" />
          <span>Scegli dal rullino</span>
        </button>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploading && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-0 left-0 right-0 bg-[#6a7263] text-white py-3 px-6 text-center z-50 font-body"
          >
            Caricamento {uploadProgress.current} di {uploadProgress.total}...
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo Counter */}
      <div className="px-6 pb-4">
        <p className="font-body text-sm text-[#6a7263]">
          {photos.length} {photos.length === 1 ? 'foto' : 'foto'}
        </p>
      </div>

      {/* Gallery */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-[#6a7263] animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <p className="font-body text-[#6a7263] text-sm">
            Ancora nessuna foto.<br />
            Sii il primo a condividere un momento della giornata!
          </p>
        </div>
      ) : (
        <div className="px-2 pb-24">
          <div className="grid grid-cols-2 gap-1">
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="relative aspect-square overflow-hidden bg-[#eddfb8]/30 cursor-pointer"
                onClick={() => setLightboxPhoto(photo)}
              >
                <img
                  src={getPhotoUrl(photo.url)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(photo.id);
                    }}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-600/80 text-white rounded-full flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* FAB Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-3 z-40">
        {/* Refresh FAB */}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="w-12 h-12 bg-[#eddfb8] text-[#6a7263] rounded-full shadow-lg flex items-center justify-center transition-transform active:scale-95"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
        
        {/* Add FAB with Menu - Salvia color */}
        <div className="relative">
          <AnimatePresence>
            {showFabMenu && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                className="absolute bottom-16 right-0 bg-white rounded-xl shadow-xl overflow-hidden min-w-[200px] font-body"
              >
                <button
                  onClick={() => {
                    cameraInputRef.current?.click();
                    setShowFabMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#4b453a] hover:bg-[#F9F8F6] text-left"
                >
                  <Camera className="w-5 h-5 text-[#6a7263]" />
                  <span>Scatta una foto</span>
                </button>
                <button
                  onClick={() => {
                    galleryInputRef.current?.click();
                    setShowFabMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#4b453a] hover:bg-[#F9F8F6] text-left border-t border-[#eddfb8]"
                >
                  <Image className="w-5 h-5 text-[#6a7263]" />
                  <span>Scegli dal rullino</span>
                </button>
                <button
                  onClick={() => setShowFabMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-[#6a7263] hover:bg-[#F9F8F6] text-left border-t border-[#eddfb8]"
                >
                  <X className="w-5 h-5" />
                  <span>Annulla</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          
          <button
            onClick={() => setShowFabMenu(!showFabMenu)}
            className="w-14 h-14 bg-[#6a7263] text-white rounded-full shadow-lg flex items-center justify-center transition-transform active:scale-95"
          >
            <Plus className={`w-6 h-6 transition-transform ${showFabMenu ? 'rotate-45' : ''}`} />
          </button>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex flex-col"
            onClick={() => setLightboxPhoto(null)}
          >
            {/* Close Button */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={() => setLightboxPhoto(null)}
                className="w-10 h-10 bg-white/20 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Image */}
            <div className="flex-1 flex items-center justify-center p-4">
              <img
                src={getPhotoUrl(lightboxPhoto.url)}
                alt=""
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            {/* Actions */}
            <div className="p-4 flex justify-center gap-4 pb-8">
              <a
                href={getPhotoUrl(lightboxPhoto.url)}
                download={`foto_${lightboxPhoto.id}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 px-6 py-3 bg-white text-[#4b453a] rounded-full font-body font-medium"
              >
                <Download className="w-5 h-5" />
                <span>Salva</span>
              </a>
              
              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(lightboxPhoto.id);
                  }}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-full font-body font-medium"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Elimina</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
